import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UseFloatingHoverOptions {
  delay?: number;
  onClose?: () => void;
  onOpen?: () => void;
}

interface UseFloatingHoverReturn {
  handleMenuMouseEnter: () => void;
  handleMenuMouseLeave: () => void;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  isOpen: boolean;
}

export const useFloatingHover = ({
  delay = 500,
  onClose,
  onOpen,
}: UseFloatingHoverOptions = {}): UseFloatingHoverReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize the clear timeout function to prevent recreation
  const clearTimeout = useCallback((): void => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Memoize the open handler to prevent recreation
  const handleOpen = useCallback((): void => {
    clearTimeout();
    if (!isOpen) {
      setIsOpen(true);
      onOpen?.();
    }
  }, [clearTimeout, isOpen, onOpen]);

  // Memoize the close handler to prevent recreation
  const handleClose = useCallback((): void => {
    clearTimeout();
    if (isOpen) {
      setIsOpen(false);
      onClose?.();
    }
  }, [clearTimeout, isOpen, onClose]);

  // Memoize the delayed close handler to prevent recreation
  const handleDelayedClose = useCallback((): void => {
    clearTimeout();
    timeoutRef.current = setTimeout(() => {
      handleClose();
    }, delay);
  }, [clearTimeout, handleClose, delay]);

  // Memoize event handlers to prevent recreation
  const eventHandlers = useMemo(
    () => ({
      handleMenuMouseEnter: handleOpen,
      handleMenuMouseLeave: handleDelayedClose,
      handleMouseEnter: handleOpen,
      handleMouseLeave: handleDelayedClose,
    }),
    [handleOpen, handleDelayedClose],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return clearTimeout;
  }, [clearTimeout]);

  return {
    ...eventHandlers,
    isOpen,
  };
};
